import type { Branch } from "../branch-settings/branch-settings.interface";
import type {
  CurrentUserResponse,
  EmployeePickerItem,
} from "../employees/employees.interface";

export type CardOrderStatus =
  (typeof CARD_ORDER_STATUS)[keyof typeof CARD_ORDER_STATUS];

export const CARD_ORDER_STATUS = {
  PENDING_APPROVAL: "PENDING_APPROVAL",
  ORDER_PLACED: "ORDER_PLACED",
  SENT_TO_PRINTER: "SENT_TO_PRINTER",
  IN_PRODUCTION: "IN_PRODUCTION",
  PRINTING_DONE: "PRINTING_DONE",
  PICKED_FROM_PRINTER: "PICKED_FROM_PRINTER",
  PACKED: "PACKED",
  UPS_SHIPPED: "UPS_SHIPPED",
  IN_TRANSIT: "IN_TRANSIT",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
} as const;

export type FulfillmentBlockedReason =
  (typeof FULFILLMENT_BLOCKED_REASON)[keyof typeof FULFILLMENT_BLOCKED_REASON];

export const FULFILLMENT_BLOCKED_REASON = {
  EMPLOYEE_DELETED: "EMPLOYEE_DELETED",
} as const;

export type CardOrderDeliveryType = "COURIER" | "ACCESS_POINT" | "PICKUP";

export type CardOrderDeliverySpeed = "STANDARD" | "EXPRESS";

export const CARD_ORDER_DELIVERY_SPEEDS: CardOrderDeliverySpeed[] = [
  "STANDARD",
  "EXPRESS",
];

export type CardOrderProductionTime = "ECONOMY" | "STANDARD" | "EXPRESS";

export const CARD_ORDER_PRODUCTION_TIMES: CardOrderProductionTime[] = [
  "ECONOMY",
  "STANDARD",
  "EXPRESS",
];

export type CardOrderShipmentCarrier = "UPS" | "OTHER";

export const CARD_ORDER_SHIPMENT_CARRIERS: CardOrderShipmentCarrier[] = [
  "UPS",
  "OTHER",
];

export interface CardOrderExternalShipment {
  carrierName: string;
  trackingNumber: string;
  trackingUrl?: string | null;
}

export type CardOrderDeliveryDestination = "branch" | "custom";

export const CARD_ORDER_CREATE_STEP = {
  Employees: 0,
  Cards: 1,
  Cart: 2,
  Checkout: 3,
  Summary: 4,
} as const;

export function clampCardOrderCreateStep(index: number): number {
  if (!Number.isFinite(index)) {
    return CARD_ORDER_CREATE_STEP.Employees;
  }
  return Math.min(
    Math.max(Math.trunc(index), CARD_ORDER_CREATE_STEP.Employees),
    CARD_ORDER_CREATE_STEP.Summary,
  );
}

export interface CardOrderShipmentMethod {
  deliveryType: CardOrderDeliveryType;
  labelKey: string;
}

export interface CardOrderShippingAddress {
  line1: string;
  line2?: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface CardOrderUpsAccessPoint {
  id: string;
  name: string;
  line1: string;
  line2?: string;
  city: string;
  postalCode: string;
  country: string;
  latitude?: number;
  longitude?: number;
  distanceKm?: number;
  openingHours?: string;
}

export interface CardOrderLineItem {
  id: string;
  templateId: string;
  templateName: string;
  templateType: string;
  pageSizePresetId: string;
  pageSizeLabelKey?: string;
  orientation?: "landscape" | "portrait";
  hasPdf?: boolean;
  quantity: number;
  printSides?: string;
  externalOrderId?: string | null;
  printerOrderNumber?: string | null;
  printerStatusLabel?: string | null;
  printerName?: string | null;
}

export interface CardOrderRecipient {
  employeeEmail: string;
  employeeName: string | null;
  fulfillmentBlockedReason?: FulfillmentBlockedReason | null;
  items: CardOrderLineItem[];
}

export interface CardOrderFulfillmentTimelineStep {
  id: CardOrderStatus;
  state: "completed" | "current" | "upcoming" | "cancelled";
  occurredAt: string | null;
  detail: string | null;
}

export interface CardOrderFulfillmentDetails {
  status: CardOrderStatus;
  timeline: CardOrderFulfillmentTimelineStep[];
  hasBlockedRecipients?: boolean;
  hasFulfillableItems?: boolean;
  actions?: {
    canCancelOrder?: boolean;
    canApproveOrder?: boolean;
  };
}

export interface CardOrderUpsTrackingActivity {
  date: string | null;
  time: string | null;
  status: string;
  city: string | null;
  country: string | null;
  statusType?: "D" | "I" | "M" | "P" | "X" | null;
  statusCode?: string | null;
}

export interface CardOrderUpsTrackingDetails {
  trackingNumber: string;
  status: string;
  activities: CardOrderUpsTrackingActivity[];
  statusType?: "D" | "I" | "M" | "P" | "X" | null;
  statusCode?: string | null;
  delivered?: boolean;
  inTransitAt?: string | null;
  deliveredAt?: string | null;
}

export interface CardOrderUpsPackageDetails {
  id: string;
  trackingNumber: string | null;
  printerOrderNumbers: string[];
  packageDescription: string | null;
  trackingStatus: string | null;
  shippingLabelGifBase64?: string | null;
  tracking?: CardOrderUpsTrackingDetails | null;
  shippedAt?: string | null;
  inTransitAt?: string | null;
  deliveredAt?: string | null;
}

export interface CardOrderUpsPackingConfig {
  shipmentDescription: string | null;
}

export interface CardOrderUpsDetails {
  deliveryType?: CardOrderDeliveryType | null;
  trackingNumber?: string | null;
  trackingStatus?: string | null;
  tracking?: CardOrderUpsTrackingDetails | null;
  packages?: CardOrderUpsPackageDetails[];
  packing?: CardOrderUpsPackingConfig | null;
}

export interface CardOrder {
  printCardOrderId: string;
  domain: string;
  placedByEmail: string;
  placedByName: string | null;
  recipientName?: string | null;
  companyName?: string | null;
  recipients: CardOrderRecipient[];
  employeeEmail: string;
  employeeName: string | null;
  items: CardOrderLineItem[];
  shippingAddress: CardOrderShippingAddress;
  phone?: string | null;
  deliveryType?: CardOrderDeliveryType | null;
  deliverySpeed?: CardOrderDeliverySpeed | null;
  productionTime?: CardOrderProductionTime | null;
  shipmentCarrier?: CardOrderShipmentCarrier | null;
  externalShipment?: CardOrderExternalShipment | null;
  notes?: string | null;
  upsAccessPoint?: CardOrderUpsAccessPoint | null;
  ups?: CardOrderUpsDetails | null;
  printer?: {
    scheduledSubmitAt?: string | null;
    submittedAt?: string | null;
  } | null;
  status: CardOrderStatus;
  fulfillment?: CardOrderFulfillmentDetails | null;
  createdAt: string;
  modifiedAt: string;
}

export interface CardOrderListResponse {
  orders: CardOrder[];
  total: number;
}

export interface CardOrderListParams {
  page: number;
  limit: number;
  phrase?: string;
}

export interface CardOrderCreateItemPayload {
  templateId: string;
  pageSizePresetId: string;
  quantity: number;
}

export interface CardOrderCreateRecipientPayload {
  employeeEmail: string;
  items: CardOrderCreateItemPayload[];
}

export interface CardOrderCreatePayload {
  recipients: CardOrderCreateRecipientPayload[];
  shippingAddress?: CardOrderShippingAddress;
  phone?: string;
  recipientName?: string;
  companyName?: string;
  deliveryType?: CardOrderDeliveryType;
  deliverySpeed?: CardOrderDeliverySpeed;
  productionTime?: CardOrderProductionTime;
  notes?: string;
  upsAccessPoint?: CardOrderUpsAccessPoint;
}

export interface CardOrderOptions {
  enabled: boolean;
  supportedPageSizePresetIds: string[];
  quantitiesByTemplateType?: {
    PAPER: number[];
    PAPER_NFC: number[];
    PLASTIC_NFC: number[];
  };
  upsEnabled: boolean;
  shipmentMethods: CardOrderShipmentMethod[];
}

export interface CardOrderCartLine {
  id: string;
  employee: EmployeePickerItem;
  templateId: string;
  templateName: string;
  pageSizePresetId: string;
  quantity: number;
}

export interface CardOrderCartEmployeeGroup {
  employee: EmployeePickerItem;
  lines: CardOrderCartLine[];
}

export interface BranchShippingAddress {
  line1: string;
  line2?: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface BranchShippingDetails {
  branchName: string;
  phone?: string;
  address: BranchShippingAddress;
}

export type PlacingUserContactSource = CurrentUserResponse & {
  fullName?: string;
  givenName?: string;
  familyName?: string;
  dynamicVariables?: Record<string, unknown>;
};

export interface DeliverableBranch {
  branch: Branch;
  shipping: BranchShippingDetails;
}

export type OrderItemPreviewRequest =
  | {
      mode: "draft";
      templateId: string;
      employeeEmail: string;
      pageSizePresetId: string;
      templateType: string;
      orientation?: "landscape" | "portrait";
      title: string;
      subtitle?: string;
    }
  | {
      mode: "order";
      orderId: string;
      previewApi: "admin" | "tenant";
      itemId: string;
      hasPdf?: boolean;
      title: string;
      subtitle?: string;
    };

export interface OrderItemsListItem {
  id?: string;
  templateId: string;
  templateName: string;
  templateType: string;
  pageSizePresetId: string;
  pageSizeLabelKey?: string;
  orientation?: "landscape" | "portrait";
  hasPdf?: boolean;
  quantity: number;
  printSides?: string;
  printerParameters?: Array<{
    labelKey: string;
    value?: string;
    valueKey?: string;
  }>;
  printerName?: string | null;
  printerOrderNumber?: string | null;
  printerStatusLabel?: string | null;
  externalOrderId?: string | null;
  printer?: {
    orderNumber?: string | null;
    statusLabel?: string | null;
  } | null;
}

export interface OrderItemsListRecipient {
  employeeEmail: string;
  employeeName: string | null;
  fulfillmentBlockedReason?: FulfillmentBlockedReason | null;
  items: OrderItemsListItem[];
}

export interface OrderItemsRowView {
  index: number;
  item: OrderItemsListItem;
}

export interface OrderItemsGroupView {
  key: string;
  employeeName: string;
  employeeEmail: string;
  fulfillmentBlockedReason?: FulfillmentBlockedReason | null;
  lineCount: number;
  totalQuantity: number;
  rows: OrderItemsRowView[];
}

export interface OrderItemsSummary {
  recipientCount: number;
  lineCount: number;
  totalQuantity: number;
}

export interface CardOrderTestShippingAddress {
  id: string;
  labelKey: string;
  line1: string;
  line2?: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface CardOrderCreateCheckoutDraft {
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
  recipientName: string;
  companyName: string;
  deliveryType: CardOrderDeliveryType;
  deliverySpeed: CardOrderDeliverySpeed;
  productionTime: CardOrderProductionTime;
  notes: string;
  accessPointId: string;
}

export interface CardOrderCreateDraft {
  version: 1;
  stepIndex: number;
  selectedEmployees: EmployeePickerItem[];
  selectedBranchIds: string[];
  activeEmployeeEmail: string | null;
  selectedTemplateId: string | null;
  selectedPageSizePresetId: string;
  selectedQuantity: number;
  cartLines: CardOrderCartLine[];
  checkoutForm: CardOrderCreateCheckoutDraft;
  deliveryDestination?: CardOrderDeliveryDestination;
  selectedDeliveryBranchId?: string | null;
}

export type CardOrderAddressValidationState =
  "idle" | "validating" | "valid" | "invalid";

export type CardOrderMappableAccessPoint = CardOrderUpsAccessPoint & {
  latitude: number;
  longitude: number;
};

export interface UpsShippingLabelDialogItem {
  index: number;
  trackingNumber: string | null;
  packageDescription: string | null;
  shippingLabelGifBase64: string;
}

export interface UpsShippingLabelsDialogParams {
  labels: UpsShippingLabelDialogItem[];
}

export interface CardOrderUpsShopRatesQuoteRequest {
  deliveryType: Extract<CardOrderDeliveryType, "COURIER" | "ACCESS_POINT">;
  recipientName: string;
  phone?: string;
  companyName?: string;
  shippingAddress: CardOrderShippingAddress;
  accessPoint?: Pick<
    CardOrderUpsAccessPoint,
    "id" | "name" | "line1" | "city" | "postalCode" | "country"
  >;
  packageWeight: number;
  weightUnit?: "KGS" | "LBS";
}
